import React, { useRef } from "react";
import { Grid2, ContainerProps, Typography, Box, Card } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ClientWorkoutsChoose from "./ClientWorkoutsChoose";
import WorkoutCardBase from "./WorkoutCardBase";
import { parseWorkoutResultValueJsonToWorkoutSection } from "../workout/utils";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import { CardPagination } from "../pagination/CardPagination";
import { WorkoutResultsSkeletonCard } from "../loading/WorkoutResultsSkeletonCard";
import { ReactComponent as WorkoutsIcon } from "../../icons/wokoutsIcon.svg";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";
import { getComponentDefaultTitle } from "../../utils/component";
import { ComponentType } from "../../constants";
import { DATE_FORMAT_DDMMYYYY } from "../../utils/date";
import { WorkoutSection as APIWorkoutSection } from "../workout/types";
import { useContainerQuery } from "../../hooks/useContainerQuery";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: "1 1 50%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    containerType: "inline-size",
    height: "100%",
    boxSizing: "border-box",
  },

  container: {
    minHeight: 100,
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    flex: 1,
    [theme.containerQueries.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  hasMore: {
    margin: theme.spacing(0, 1),
  },
  noText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    width: "100%",
    textAlign: "center",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    paddingTop: theme.spacing(1),
    marginTop: "auto",
  },
}));

export interface ClientWorkoutsProps extends Omit<ContainerProps, "children"> {
  clientId: number;
  username: string;
  isPreview?: boolean;
  isFirst?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}

const CLIENT_WORKOUTS_PAGE_SIZE_SM_UP = 6;
const CLIENT_WORKOUTS_PAGE_SIZE_SM = 3;
export const CLIENT_WORKOUTS_LIST_QUERY_KEY = "client-workouts-list";

function ClientWorkouts(props: ClientWorkoutsProps) {
  const { clientId, username, isPreview, isFirst, collapsable, defaultOpen } =
    props;

  const [selectWorkout, setSelectWorkout] = React.useState(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpenContent, setIsOpenContent] = React.useState(!!defaultOpen);
  const chooseWorkoutItem = React.useCallback(
    (data) => setSelectWorkout(data),
    [],
  );

  const handleCloseWorkout = React.useCallback(
    () => setSelectWorkout(null),
    [],
  );

  const sumSessions = React.useCallback(
    (sections: APIWorkoutSection[] = []) => {
      return sections.reduce(
        (sum, section) =>
          sum +
          section.exercises.reduce((s, ex) => s + (ex.result?.length ?? 0), 0),
        0,
      );
    },
    [],
  );
  const sumLbs = React.useCallback((sections: APIWorkoutSection[] = []) => {
    return sections.reduce(
      (total, section) =>
        total +
        section.exercises.reduce(
          (s, ex) =>
            s +
            (ex.result?.reduce((r, i) => r + (Number(i.weight) || 0), 0) ?? 0),
          0,
        ),
      0,
    );
  }, []);

  const timeWorkout = React.useCallback((value) => {
    const timeWorkout = value ? JSON.parse(value) : { time: 0 };
    return timeWorkout.time;
  }, []);

  const pageSize = collapsable
    ? CLIENT_WORKOUTS_PAGE_SIZE_SM
    : CLIENT_WORKOUTS_PAGE_SIZE_SM_UP;
  const [currentPage, setCurrentPage] = React.useState(1);

  const {
    data: groupedData,
    isFetching,
    isPlaceholderData,
    isLoading,
  } = useQuery({
    queryKey: [CLIENT_WORKOUTS_LIST_QUERY_KEY, { clientId, currentPage }],
    queryFn: () =>
      WorkoutResultsService.getUserWorkoutGroups(
        currentPage,
        pageSize,
        clientId,
      ),
    placeholderData: keepPreviousData,
  });

  const s = useStyles();

  const workoutExerciseResults = React.useMemo(() => {
    if (!groupedData?.items) return [];
    return groupedData.items.map(({ activity, values }) => {
      const workoutTitle =
        activity?.component?.title ??
        getComponentDefaultTitle(ComponentType.WORKOUT);
      const parsedArray = Array.isArray(values) ? values : [];

      const sections: APIWorkoutSection[] = parsedArray.map((v) =>
        parseWorkoutResultValueJsonToWorkoutSection(v),
      );

      const parsedStart = activity.startWorkout
        ? JSON.parse(activity.startWorkout)
        : {};

      const parsedEnd = activity.endWorkout
        ? JSON.parse(activity.endWorkout)
        : {};

      return {
        activity: {
          ...activity,
          title: workoutTitle,
          parsedStart,
          parsedEnd,
        },
        sections,
      };
    });
  }, [groupedData]);

  const showCards = workoutExerciseResults.length > 0 && !isPlaceholderData;
  const showSkeleton = isLoading || (isFetching && isPlaceholderData);
  const showEmpty = !showCards && !showSkeleton;

  return (
    <Card
      ref={containerRef}
      className={clsx(s.root, JourneyAnchors.CLIENT_WORKOUTS)}
      elevation={0}
      variant="outlined"
    >
      <ClientDetailsCardHeader
        title="Workouts"
        icon={<ProfileIconBlock icon={<WorkoutsIcon />} bgVariant="accent" />}
        isOpenContent={isOpenContent}
        setIsOpenContent={setIsOpenContent}
        isPreview={isPreview}
        isFirst={isFirst}
        collapsable={collapsable}
        defaultOpen={defaultOpen}
      />

      {isOpenContent && (
        <Grid2 container spacing={2} className={s.container}>
          {selectWorkout ? (
            <Box className={s.container}>
              <ClientWorkoutsChoose
                onClose={handleCloseWorkout}
                selectWorkout={selectWorkout}
                username={username}
              />
            </Box>
          ) : (
            <>
              {showCards &&
                workoutExerciseResults.map(({ activity, sections }, index) => (
                  <Box key={index} sx={{ width: "100%" }}>
                    <WorkoutCardBase
                      title={activity.title}
                      subtitle={`Recorded ${activity.completedAt.format(DATE_FORMAT_DDMMYYYY)}`}
                      onClick={() =>
                        chooseWorkoutItem({
                          title: activity.title,
                          date: activity.date,
                          completedAt: activity.completedAt,
                          programSlug: activity?.programSlug,
                          componentSlug: activity?.component?.slug,
                          session: sumSessions(sections),
                          min: activity.parsedEnd.time ?? 0,
                          lbs: sumLbs(sections),
                          mindValue: activity.parsedStart.mindValue,
                          bodyValue: activity.parsedStart.bodyValue,
                          feel: activity.parsedEnd.feel,
                          value: sections.map((section) => ({
                            title: section.title,
                            result: section.exercises,
                          })),
                        })
                      }
                    />
                  </Box>
                ))}

              {showSkeleton &&
                Array.from({ length: pageSize }).map((_, i) => (
                  <WorkoutResultsSkeletonCard key={i} />
                ))}

              {showEmpty && (
                <Typography className={s.noText}>
                  No workouts to show.
                </Typography>
              )}

              {groupedData?.totalPages > 1 && (
                <CardPagination
                  page={currentPage}
                  count={groupedData.totalPages}
                  boundaryCount={1}
                  className={s.pagination}
                  onChange={(_, page) => setCurrentPage(page)}
                />
              )}
            </>
          )}
        </Grid2>
      )}
    </Card>
  );
}

export default React.memo(ClientWorkouts);
